import { Field, Input, type InputProps } from '@fluentui/react-components';
import { CheckmarkRegular, ClipboardPasteFilled, EyeRegular } from '@fluentui/react-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { useInputAppearance } from '../hooks';
import { Tooltip } from '../Tooltip';
import './PasswordPreviewInput.css';

export interface PasswordPreviewInputProps {
  label?: string;
  initialValue: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  appearance?: InputProps['appearance'];
}

type InputType = 'password' | 'text';

export const PasswordPreviewInput: React.FC<PasswordPreviewInputProps> = ({
  label,
  initialValue,
  onChange,
  disabled,
  appearance,
}) => {
  const { t } = useTranslation();
  const themedAppearance = useInputAppearance();
  const [type, setType] = useState<InputType>('password');
  const [value, setValue] = useState<string>(initialValue);
  const [isCopiedToClipboard, setIsCopiedToClipboard] = useState<boolean>(false);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const toggleInputType = () => {
    setType(type === 'password' ? 'text' : 'password');
  };

  const handlePasswordInputChange = (event_: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event_.target.value ?? '';
    setIsCopiedToClipboard(false);
    setValue(newValue);
    onChange(newValue);
  };

  const copyToClipboard = async () => {
    try {
      navigator.clipboard.writeText(value);
      setIsCopiedToClipboard(true);
    } catch {
      // Failed to copy
    }
  };

  return (
    <div className="flex items-end gap-1 passwordPreviewInput">
      <Field label={label} className="flex-1">
        <Input
          appearance={appearance ?? themedAppearance}
          type={type}
          value={value}
          onChange={handlePasswordInputChange}
          disabled={disabled}
        />
      </Field>
      <Tooltip
        content={
          type === 'text'
            ? t('passwordPreviewInput_hidePassword', { defaultValue: 'Passwort verbergen' })
            : t('passwordPreviewInput_showPassword', { defaultValue: 'Passwort anzeigen' })
        }
        trigger={
          <Button
            className={type === 'text' ? 'passwordPreviewInput__toggle--active' : undefined}
            icon={<EyeRegular />}
            onClick={toggleInputType}
            aria-pressed={type === 'text'}
          />
        }
      />
      <Tooltip
        content={t('passwordPreviewInput_copyToClipboard', { defaultValue: 'Password kopieren' })}
        trigger={
          <Button
            icon={
              isCopiedToClipboard ? (
                <CheckmarkRegular className="text-success" />
              ) : (
                <ClipboardPasteFilled />
              )
            }
            onClick={copyToClipboard}
          />
        }
      />
    </div>
  );
};
